import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { customerService, type CustomerContact } from '@/services/customer.service';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';

/**
 * Add or edit a person at a location, without leaving the intake screen.
 *
 * A contact cannot exist without a location — the column is NOT NULL and the row
 * is deleted with its location — so `locationId` is required here and the caller
 * must not open this until a location is chosen.
 *
 * Replaces the older add-only modal, which never captured email even though the
 * API has always accepted it.
 */

export type ContactFormMode = { kind: 'create' } | { kind: 'edit'; contact: CustomerContact };

interface Fields {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  designation: string;
}

const EMPTY: Fields = { firstName: '', lastName: '', phone: '', email: '', designation: '' };

function fieldsFor(mode: ContactFormMode): Fields {
  if (mode.kind === 'create') return EMPTY;
  const c = mode.contact;
  return {
    firstName: c.firstName,
    lastName: c.lastName,
    phone: c.phone ?? '',
    email: c.email ?? '',
    designation: c.designation ?? '',
  };
}

function apiMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function ContactFormModal({
  open,
  customerId,
  locationId,
  locationName,
  mode,
  onClose,
  onSaved,
}: {
  open: boolean;
  customerId: string;
  locationId: string;
  /** Named in the title so the operator can see which place they are adding to. */
  locationName: string;
  mode: ContactFormMode;
  onClose: () => void;
  onSaved: (contact: CustomerContact) => void;
}) {
  const queryClient = useQueryClient();
  const [f, setF] = useState<Fields>(EMPTY);

  useEffect(() => {
    if (open) setF(fieldsFor(mode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = <K extends keyof Fields>(k: K, v: Fields[K]) => setF((p) => ({ ...p, [k]: v }));

  const mutation = useMutation({
    mutationFn: async (): Promise<CustomerContact> => {
      const payload = {
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        phone: f.phone.trim(),
        email: f.email.trim() || undefined,
        designation: f.designation.trim() || undefined,
      };
      return mode.kind === 'edit'
        ? customerService.updateContact(customerId, mode.contact.id, payload)
        : customerService.createContact(customerId, locationId, payload);
    },
    onSuccess: (contact) => {
      void queryClient.invalidateQueries({
        queryKey: ['customer-location-contacts', customerId, locationId],
      });
      toast.success(mode.kind === 'edit' ? 'Contact updated' : 'Contact added');
      onSaved(contact);
      onClose();
    },
    onError: (error: Error) => toast.error(apiMessage(error, 'Could not save the contact')),
  });

  const canSubmit = !!f.firstName.trim() && !!f.lastName.trim() && !!f.phone.trim() && !!locationId;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        mode.kind === 'edit' ? `Edit ${mode.contact.firstName}` : `Add a contact — ${locationName}`
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) mutation.mutate();
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="First name" htmlFor="ct-first" required>
            <Input
              id="ct-first"
              value={f.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              disabled={mutation.isPending}
              required
            />
          </FormField>
          <FormField label="Last name" htmlFor="ct-last" required>
            <Input
              id="ct-last"
              value={f.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              disabled={mutation.isPending}
              required
            />
          </FormField>
        </div>

        <FormField label="Mobile" htmlFor="ct-phone" required>
          <Input
            id="ct-phone"
            value={f.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="+91 9876543210"
            disabled={mutation.isPending}
            required
          />
        </FormField>

        <FormField label="Email" htmlFor="ct-email">
          <Input
            id="ct-email"
            type="email"
            value={f.email}
            onChange={(e) => set('email', e.target.value)}
            disabled={mutation.isPending}
          />
        </FormField>

        <FormField label="Designation" htmlFor="ct-desig">
          <Input
            id="ct-desig"
            value={f.designation}
            onChange={(e) => set('designation', e.target.value)}
            placeholder="Service Manager"
            disabled={mutation.isPending}
          />
        </FormField>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
